/**
 * Gate 1 — Anchor Inversion
 *
 * Detects positive-valence anchoring without counterfactual.
 * An output that stakes a position on a positive outcome while
 * providing no failure-mode, caveat, or alternative-scenario
 * acknowledgment in a declarative context is anchoring on the
 * upside without structural balance.
 *
 * Trip routes to abstention with reason='anchor_inversion_detected'.
 */

const POSITIVE_VALENCE = [
  'will succeed', 'will achieve', 'will improve', 'will increase', 'will deliver',
  'guaranteed', 'certainly', 'definitely', 'undoubtedly', 'clearly will',
  'best outcome', 'optimal result', 'strong recommendation', 'highly recommend',
  'confident that', 'assured', 'without doubt',
];

const COUNTERFACTUAL_MARKERS = [
  'however', 'but ', 'unless', 'except ', 'risk', 'fail', 'failure', 'downside',
  'challenge', 'caveat', 'alternatively', 'though ', 'although', 'if not',
  'provided that', 'assuming ', 'contingent', 'concern', 'limitation',
  'drawback', 'caution', 'warn', 'might not', 'could fail', 'may not',
  'not guaranteed', 'no guarantee', 'uncertain',
];

const POSITIVE_VALENCE_DENSITY_THRESHOLD = 2;

/**
 * @param {import('../disciplineEnforcer.js').EnforcerEnvelope} envelope
 * @param {object} [_config]
 * @returns {import('../disciplineEnforcer.js').GateResult}
 */
export function checkAnchorInversion(envelope, _config = {}) {
  const text = (envelope.rawOutput || '').toLowerCase();

  if (!text) {
    return { gateName: 'anchor_inversion', verdict: 'pass', reason: null, payload: null };
  }

  const positiveHits = POSITIVE_VALENCE.filter(p => text.includes(p));
  const hasCounterfactual = COUNTERFACTUAL_MARKERS.some(c => text.includes(c));

  if (positiveHits.length >= POSITIVE_VALENCE_DENSITY_THRESHOLD && !hasCounterfactual) {
    return {
      gateName: 'anchor_inversion',
      verdict: 'trip',
      reason: 'anchor_inversion_detected',
      payload: { positiveHits, counterfactualFound: false },
    };
  }

  return {
    gateName: 'anchor_inversion',
    verdict: 'pass',
    reason: null,
    payload: { positiveHits, counterfactualFound: hasCounterfactual },
  };
}
